import Seo from "@/components/Seo";
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Search, ExternalLink, FileText, Users } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Empty } from "@/components/Shared";

const CAT_COLORS = {
  "cs.AI": "bg-[#8B5CF6]/15 text-[#a78bfa] border-[#8B5CF6]/30",
  "cs.LG": "bg-[#00F0FF]/15 text-[#00F0FF] border-[#00F0FF]/30",
  "cs.CL": "bg-[#22C55E]/15 text-[#4ade80] border-[#22C55E]/30",
};

const relDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch { return ""; }
};

const authorLine = (authors = []) => {
  if (!authors.length) return "";
  if (authors.length <= 3) return authors.join(", ");
  return `${authors.slice(0, 3).join(", ")} +${authors.length - 3} more`;
};

const snippet = (text = "", n = 320) => {
  const t = (text || "").trim();
  if (t.length <= n) return t;
  return t.slice(0, n).replace(/\s+\S*$/, "") + "…";
};

const CatTag = ({ cat }) => (
  <span className={`font-mono-tech text-[10px] uppercase px-2 py-0.5 rounded border ${CAT_COLORS[cat] || "bg-white/5 text-[#94A3B8] border-white/10"}`}>
    {cat}
  </span>
);

const SOURCE_META = {
  arxiv: { label: "arXiv", cls: "bg-[#B31B1B]/15 text-[#ff8a8a] border-[#B31B1B]/40" },
  huggingface: { label: "Hugging Face", cls: "bg-[#FFD21E]/15 text-[#FFD21E] border-[#FFD21E]/40" },
};

const SourceTag = ({ source }) => {
  const m = SOURCE_META[source] || { label: source, cls: "bg-white/5 text-[#94A3B8] border-white/10" };
  return (
    <span data-testid={`paper-source-${source}`}
      className={`font-mono-tech text-[10px] uppercase px-2 py-0.5 rounded border ${m.cls}`}>
      {m.label}
    </span>
  );
};

export default function Papers() {
  const [papers, setPapers] = useState([]);
  const [cats, setCats] = useState([]);
  const [sources, setSources] = useState([]);
  const [activeCat, setActiveCat] = useState("All");
  const [activeSource, setActiveSource] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = (category, source, q) => {
    setLoading(true);
    const params = {};
    if (category && category !== "All") params.category = category;
    if (source && source !== "All") params.source = source;
    if (q) params.search = q;
    api.get("/papers", { params })
      .then((r) => {
        setPapers(r.data.papers || []);
        setCats(r.data.categories || []);
        setSources(r.data.sources || []);
        setLastUpdated(r.data.last_updated || null);
      })
      .catch((err) => toast.error(formatApiError(err?.response?.data?.detail) || "Could not load papers"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(activeCat, activeSource, search); /* eslint-disable-next-line */ }, [activeCat, activeSource]);

  const onSearch = (e) => {
    e.preventDefault();
    load(activeCat, activeSource, search);
  };

  return (
    <div className="min-h-screen" data-testid="papers-page">
      <Seo
        title="AI Research Papers — Latest from arXiv & Hugging Face | WorldAIInfo"
        description="The newest AI, machine-learning and NLP research papers, auto-updated from arXiv and Hugging Face. Read the original abstract and link straight to the paper and PDF."
        canonicalPath="/papers" />

      <PageHeader
        overline="Research"
        title="AI Research Papers"
        subtitle="The latest AI, machine-learning and language-model research, pulled fresh from arXiv and Hugging Face. Abstracts shown as published by the authors — read in full at the source."
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 pb-20">
        {/* Controls */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
          <div className="flex flex-wrap gap-2" data-testid="papers-category-chips">
            {["All", ...cats.map((c) => c.key)].map((key) => {
              const label = key === "All" ? "All" : (cats.find((c) => c.key === key)?.label || key);
              const active = activeCat === key;
              return (
                <button key={key} onClick={() => setActiveCat(key)}
                  data-testid={`papers-chip-${key.replace(/\./g, "-")}`}
                  className={`font-mono-tech text-xs uppercase px-3 py-1.5 rounded-full border transition-colors ${active
                    ? "bg-[#00F0FF] text-[#04121a] border-[#00F0FF]"
                    : "text-[#94A3B8] border-white/10 hover:border-[#00F0FF]/50 hover:text-[#00F0FF]"}`}>
                  {label}{key !== "All" ? ` · ${key}` : ""}
                </button>
              );
            })}
          </div>
          <form onSubmit={onSearch} className="md:ml-auto relative w-full md:w-80" data-testid="papers-search-form">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search title, abstract, author…" data-testid="papers-search-input"
              className="w-full bg-[#111827] border border-white/10 rounded-md pl-9 pr-3 py-2.5 text-sm focus:border-[#00F0FF] focus:outline-none" />
          </form>
        </div>

        {/* Source filter chips */}
        <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="papers-source-chips">
          <span className="font-mono-tech text-[10px] uppercase text-[#64748B] mr-1">Source</span>
          {["All", ...sources.map((s) => s.key)].map((key) => {
            const label = key === "All" ? "All" : (sources.find((s) => s.key === key)?.label || key);
            const active = activeSource === key;
            return (
              <button key={key} onClick={() => setActiveSource(key)}
                data-testid={`papers-source-chip-${key}`}
                className={`font-mono-tech text-xs uppercase px-3 py-1.5 rounded-full border transition-colors ${active
                  ? "bg-[#FFD21E] text-[#04121a] border-[#FFD21E]"
                  : "text-[#94A3B8] border-white/10 hover:border-[#FFD21E]/60 hover:text-[#FFD21E]"}`}>
                {label}
              </button>
            );
          })}
        </div>

        {lastUpdated && (
          <p className="text-xs text-[#64748B] mb-6" data-testid="papers-last-updated">
            Auto-updated from arXiv &amp; Hugging Face · last refreshed {relDate(lastUpdated)}
          </p>
        )}

        {loading ? (
          <p className="text-[#94A3B8]" data-testid="papers-loading">Loading papers…</p>
        ) : papers.length === 0 ? (
          <Empty text="No papers yet — fresh research papers will appear here shortly. Please check back soon." />
        ) : (
          <div className="space-y-4" data-testid="papers-list">
            {papers.map((p, i) => (
              <motion.article key={p.id || p.arxiv_id || p.title}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: Math.min(i * 0.03, 0.3) }}
                data-testid={`paper-card-${p.arxiv_id || p.id}`}
                className="rounded-xl border border-white/10 bg-white/[0.02] hover:border-[#00F0FF]/30 transition-colors p-5 md:p-6">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <SourceTag source={p.source} />
                  {(p.categories && p.categories.length ? p.categories : [p.primary_category]).slice(0, 4)
                    .filter(Boolean).map((c) => <CatTag key={c} cat={c} />)}
                  <span className="text-xs text-[#64748B] ml-auto">{relDate(p.published_at)}</span>
                </div>
                <h2 className="font-display font-semibold text-lg text-white leading-snug" data-testid="paper-title">
                  {p.title}
                </h2>
                {p.authors && p.authors.length > 0 && (
                  <p className="mt-1.5 flex items-center gap-1.5 text-sm text-[#94A3B8]" data-testid="paper-authors">
                    <Users className="w-3.5 h-3.5 shrink-0" /> {authorLine(p.authors)}
                  </p>
                )}
                <p className="mt-3 text-sm text-[#CBD5E1] leading-relaxed" data-testid="paper-abstract">
                  {snippet(p.abstract)}
                </p>
                <div className="mt-4 flex items-center gap-3">
                  {p.abstract_url ? (
                    <a href={p.abstract_url} target="_blank" rel="noopener noreferrer"
                      data-testid="paper-arxiv-link"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#00F0FF] hover:brightness-110">
                      <ExternalLink className="w-4 h-4" /> View on arXiv
                    </a>
                  ) : p.hf_url ? (
                    <a href={p.hf_url} target="_blank" rel="noopener noreferrer"
                      data-testid="paper-hf-link"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#FFD21E] hover:brightness-110">
                      <ExternalLink className="w-4 h-4" /> View on Hugging Face
                    </a>
                  ) : null}
                  {p.pdf_url && (
                    <a href={p.pdf_url} target="_blank" rel="noopener noreferrer"
                      data-testid="paper-pdf-link"
                      className="inline-flex items-center gap-1.5 text-sm text-[#94A3B8] border border-white/10 rounded-md px-3 py-1.5 hover:border-[#00F0FF]/50 hover:text-[#00F0FF] transition-colors">
                      <FileText className="w-4 h-4" /> PDF
                    </a>
                  )}
                </div>
              </motion.article>
            ))}
          </div>
        )}

        <p className="mt-10 text-xs text-[#64748B]" data-testid="papers-attribution">
          Papers and abstracts are sourced from <a href="https://arxiv.org" target="_blank" rel="noopener noreferrer" className="text-[#00F0FF] hover:underline">arXiv.org</a> and <a href="https://huggingface.co/papers" target="_blank" rel="noopener noreferrer" className="text-[#FFD21E] hover:underline">Hugging Face Papers</a>. Thank you to both for open access. Abstracts are shown as published by their authors; we do not modify them.
        </p>
      </div>
    </div>
  );
}
